import React, { useState, useMemo } from 'react';

import {
  faChevronDown,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  Alert,
  Badge,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Pagination,
  Paper,
  Stack,
  Text,
  Title
} from '@mantine/core';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';

import EncounterCard from './EncounterCard';
import EncounterDetails from './EncounterDetails';
import { type Encounter } from './types';
import { usePostJSON } from '../../utils/request';

interface ClientWithEncounters {
  id: number;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  dob?: string;
  carePlan?: string;
  accountId: number;
  createdAt: string;
  updatedAt: string;
  _encounters: Encounter[];
}

interface ClientEncounterResponse {
  success: boolean;
  clients: ClientWithEncounters[];
  total: number;
  page: number;
  limit: number;
}

interface ClientEncounterListProps {
  title?: string;
  titleAction?: React.ReactNode;
}

const ClientEncounterList: React.FC<ClientEncounterListProps> = ({
  title = 'Recent Encounters (Past Week)',
  titleAction
}) => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);

  // Calculate date range for the past week
  const dateRange = useMemo(() => {
    const endDate = moment().endOf('day').toISOString();
    const startDate = moment().subtract(7, 'days').startOf('day').toISOString();
    return { startDate, endDate };
  }, []);

  // Hook for fetching client encounters
  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = usePostJSON<ClientEncounterResponse>(
    '/api/encounter/data_by_range',
    {
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    },
    [page, limit, dateRange.startDate, dateRange.endDate]
  );

  const handleViewDetails = (encounter: Encounter) => {
    setSelectedEncounter(encounter);
    setDetailsDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDrawerOpen(false);
    setSelectedEncounter(null);
  };

  const totalPages = Math.ceil((data?.total || 0) / limit);
  const clients = data?.clients || [];
  const total = data?.total || 0;

  if (error) {
    return (
      <Paper withBorder p="md" style={{ position: 'relative' }}>
        <Alert color="red" title="Error">
          {error.message}
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />

        <Group justify="space-between" mb="md">
          <Group gap="md">
            <Title order={3}>{title}</Title>
            {titleAction}
          </Group>
          <Badge color="blue" variant="light">
            {total} {total === 1 ? 'client' : 'clients'}
          </Badge>
        </Group>

        {clients.length === 0 ? (
          <Text c="dimmed" py="xl" ta="center">
            No clients with encounters found in the past week.
          </Text>
        ) : (
          <>
            <Stack gap="md">
              {clients.map((client) => {
                const encounters = client._encounters || [];
                const mostRecentEncounter = encounters[0]; // Already sorted by createdAt desc
                const otherEncounters = encounters.slice(1);

                return (
                  <Card key={client.id} withBorder p="md" shadow="sm">
                    <Stack gap="md">
                      {/* Client Header */}
                      <Group gap="sm" justify="space-between">
                        <Group gap="sm">
                          <FontAwesomeIcon icon={faUser} size="lg" />
                          <Text
                            c="blue"
                            component={Link}
                            fw={600}
                            size="lg"
                            style={{
                              textDecoration: 'none',
                              color: 'inherit'
                            }}
                            to={`/client/${client.id}/encounters`}
                          >
                            {client.firstName} {client.lastName}
                          </Text>
                          {otherEncounters.length > 0 && (
                            <Badge color="gray" variant="light">
                              {encounters.length} {encounters.length === 1 ? 'encounter' : 'encounters'}
                            </Badge>
                          )}
                        </Group>
                      </Group>

                      {/* Most Recent Encounter */}
                      {mostRecentEncounter && (
                        <EncounterCard
                          encounter={mostRecentEncounter}
                          onViewDetails={handleViewDetails}
                        />
                      )}

                      {/* Accordion for Other Encounters */}
                      {otherEncounters.length > 0 && (
                        <Accordion
                          chevron={<FontAwesomeIcon icon={faChevronDown} />}
                          variant="separated"
                        >
                          <Accordion.Item value="other-encounters">
                            <Accordion.Control>
                              <Text fw={500}>
                                View {otherEncounters.length} {otherEncounters.length === 1 ? 'other encounter' : 'other encounters'}
                              </Text>
                            </Accordion.Control>
                            <Accordion.Panel>
                              <Stack gap="xs" mt="xs">
                                {otherEncounters.map((encounter) => (
                                  <EncounterCard
                                    key={encounter.id}
                                    encounter={encounter}
                                    onViewDetails={handleViewDetails}
                                  />
                                ))}
                              </Stack>
                            </Accordion.Panel>
                          </Accordion.Item>
                        </Accordion>
                      )}
                    </Stack>
                  </Card>
                );
              })}
            </Stack>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                />
              </Group>
            )}
          </>
        )}
      </div>

      {/* Encounter Details Drawer */}
      <Drawer
        opened={detailsDrawerOpen}
        position="right"
        size="lg"
        title={
          selectedEncounter ?
            `Encounter Details - ${selectedEncounter._client.firstName} ${selectedEncounter._client.lastName}` :
            'Encounter Details'
        }
        onClose={handleCloseDetails}
      >
        {selectedEncounter && (
          <EncounterDetails
            encounter={selectedEncounter}
            onClose={handleCloseDetails}
            onRefetch={refetch}
          />
        )}
      </Drawer>
    </>
  );
};

export default ClientEncounterList;
